// frontend/src/pages/AuditorPage.tsx
// SISTEMA OMEGA: MÓDULO AUDITOR-AI (v1.1.0 - LINTER CLEAN)
// NIVEL: CRÍTICO | UX: DARK PREMIUM

import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import {
    Box, Container, Typography, Paper, Stepper, Step, StepLabel,
    Button, CircularProgress, Grid, TextField, Select, MenuItem,
    FormControl, InputLabel, Switch, FormControlLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, AlertTitle, Chip, Card, CardContent, Divider, Dialog, DialogActions, DialogContent, DialogTitle, Stack
} from '@mui/material';


// Iconos Utilizados
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';

// Recursos Internos
import { AUDITOR_FIELDS, AuditorField } from '../config/auditorFields';
import { 
    ingestAuditFile, 
    calculateAuditStats, 
    downloadAuditReport,
    upsertAuditCompliance,
    analyzeAuditorFileNormalization,
    reviewAuditorFileNormalization
} from '../services/auditorService';
import {
    ReglaConcepto,
    AuditCalculationStats,
    ComplianceMode,
    ComplianceContext,
    AuditorFileNormalizationResult,
    ReportProfileId,
    ComparisonDisplayMode
} from '../types/auditor';

// --- ESTILOS ---
const glassCardSx = {
    bgcolor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 2,
    p: 4,
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
};

const uploadZoneSx = {
    border: '2px dashed #334155',
    borderRadius: 2,
    p: 6,
    textAlign: 'center' as const,
    cursor: 'pointer',
    bgcolor: 'rgba(255, 255, 255, 0.02)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        bgcolor: 'rgba(56, 189, 248, 0.05)',
        borderColor: '#38bdf8'
    }
};

interface LocalFileState {
    file: File | null;
    headers: string[];
    clientName: string;
    anioAnalisis: string;
    selectedSheet: string | null;
    headerRowIndex: number | null;
}

// Nota: “concepto_ministerio” se mantiene solo como campo técnico para compatibilidad con backend.
// En UI trabajamos con clasificación operativa (Salarial/Extrasalarial + Normalizable/Anualizable) y toggle Incluir/Excluir.

const AuditorPage: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();

    const normalizeHeader = (value: string): string =>
        String(value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/\xa0/g, ' ')
            .replace(/[._-]/g, ' ')
            .replace(/[ºª]/g, '')
            .replace(/[%]/g, '%')
            .replace(/\s+/g, ' ')
            .trim();

    const FIELD_ALIASES: Record<string, string[]> = {
        id_trabajador: [
            'id trabajador',
            'id_trabajador',
            'id',
            'codigo',
            'código',
            'nif'
        ],
        sexo: [
            'sexo',
            'genero',
            'género'
        ],
        fecha_nacimiento: [
            'fecha nacimiento',
            'fecha_nacimiento',
            'f nacimiento',
            'f nac',
            'fecha nac'
        ],
        nivel_estudios: [
            'nivel estudios',
            'nivel_estudios'
        ],
        fecha_inicio_contrato: [
            'fecha inicio contrato',
            'fecha_inicio_contrato',
            'fecha de contratacion',
            'fecha de contratación',
            'fecha contratacion',
            'fecha contratación',
            'f contratacion',
            'f contratación',
            'f inicio contrato',
            'fecha antiguedad',
            'fecha antigüedad',
            'antiguedad',
            'antigüedad'
        ],
        fecha_fin_contrato: [
            'fecha fin contrato',
            'fecha_fin_contrato',
            'f fin contrato',
            'fin contrato'
        ],
        tipo_contrato: [
            'tipo contrato',
            'tipo_contrato'
        ],
        porcentaje_jornada: [
            '% jornada',
            '% de jornada',
            'porcentaje jornada',
            'jornada',
            'jornada laboral',
            'jornada_laboral'
        ],
        porcentaje_jornada_reducida: [
            '% jornada reducida',
            '% de jornada reducida',
            'porcentaje jornada reducida',
            'jornada reducida'
        ],
        agrupacion_1: [
            'agrupacion 1',
            'agrupación 1',
            'agrup 1',
            'agrup clas prof',
            'agrup clasificacion profesional',
            'agrup clasificación profesional',
            'grupo profesional',
            'grupo_profesional',
            'clasificacion profesional',
            'clasificación profesional',
            'agrup clas prof'
        ],
        agrupacion_2: [
            'agrupacion 2',
            'agrupación 2',
            'agrup 2',
            'agrup valor pto',
            'agrup valor puesto',
            'valoracion puesto',
            'valoración puesto',
            'grupo valoracion',
            'grupo valoración'
        ],
        puesto_trabajo: [
            'puesto de trabajo',
            'puesto trabajo',
            'puesto',
            'puesto empresa',
            'descripcion puesto',
            'descripción puesto'
        ],
        departamento: [
            'departamento',
            'dpto'
        ],
        salario_base_anual: [
            's base',
            'sbase',
            'salario base anual',
            'salario_base_anual',
            'salario base'
        ],
        check_periodo_ref: [
            'check periodo ref',
            'periodo ref',
            'check periodo referencia'
        ],
        check_equiparado: [
            'check equiparado',
            'equiparado'
        ],
        check_n_sc_norm: [
            'check n sc norm',
            'check nº sc norm',
            'n sc norm',
            'nº sc norm'
        ],
        check_n_sc_anualiz: [
            'check n sc anualiz',
            'check nº sc anualiz',
            'n sc anualiz',
            'nº sc anualiz'
        ],
        check_n_sc_norm_y_anualiz: [
            'check n sc norm y anualiz',
            'check nº sc norm y anualiz',
            'n sc norm y anualiz',
            'nº sc norm y anualiz'
        ],
        check_equiparacion: [
            'check equiparacion',
            'check equiparación',
            'equiparacion',
            'equiparación'
        ]
    };

    const CONCEPT_NAME_CATALOG: Record<string, string> = {
        'S.BASE': 'Salario Base',
        'Conc.Sal.01': 'Antigüedad',
        'Conc.Sal.02': 'Gratificación extraordinaria',
        'Conc.Sal.03': 'Comp. Variab. Despl. Insular',
        'Conc.Sal.04': 'Comp. Salarial absorbible',
        'Conc.Sal.05': 'Plus movilidad Geográfica',
        'Conc.Sal.06': 'Plus Carencia incentivos',
        'Conc.Sal.07': 'No concurrencia futura',
        'Conc.Sal.08': 'Retribución bruta',
        'Conc.Sal.09': '41 - PLUS TEMPORAL COMPENS.4º TURNO',
        'Conc.Sal.10': '42 - PLUS TURNICIDAD 4ª TURNO',
        'Conc.Sal.11': '43 - PLUS CONTINUIDAD DE PRODUCCION',
        'Conc.Sal.12': '44 - GUARDIA',
        'Conc.Sal.13': '46 - PLUS ESPECIAL DISPONIBILIDAD',
        'Conc.Sal.14': '48 - COMPLEMENTO PUESTO FIN SEMANA',
        'Conc.Sal.15': '50 - PLUS RESPONSABLE TURNO',
        'Conc.Sal.16': '53 - PLUS NOCTURNIDAD',
        'Conc.Sal.17': '59 - Anticipo a cuenta de objetivos',
        'Conc.Sal.18': '60 - COMISION',
        'Conc.Sal.19': 'Complemento puesto',
        'Conc.Sal.20': 'Plus turnicidad',
        'Conc.Sal.21': 'Horas extra',
        'Conc.Sal.22': 'Horas presencia',
        'C.Extra.01': 'Dietas y gastos',
        'C.Extra.02': 'Prest. Enfermedad empresa',
        'C.Extra.03': 'Enfermedad',
        'C.Extra.04': 'Accidente',
        'C.Extra.05': 'Complemento IT',
    };

    const KNOWN_CONCEPT_RULES: Record<string, ReglaConcepto> = {
        'Conc.Sal.01': {
            nombre_concepto_cliente: 'Conc.Sal.01',
            nombre_informe: 'Antigüedad',
            concepto_ministerio: 'Conc.Sal.01',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.02': {
            nombre_concepto_cliente: 'Conc.Sal.02',
            nombre_informe: 'Gratificación extraordinaria',
            concepto_ministerio: 'Conc.Sal.02',
            es_salarial: true,
            es_normalizable: false,
            es_anualizable: false
        },
        'Conc.Sal.03': {
            nombre_concepto_cliente: 'Conc.Sal.03',
            nombre_informe: 'Comp. Variab. Despl. Insular',
            concepto_ministerio: 'Conc.Sal.03',
            es_salarial: true,
            es_normalizable: false,
            es_anualizable: true
        },
        'Conc.Sal.04': {
            nombre_concepto_cliente: 'Conc.Sal.04',
            nombre_informe: 'Comp. Salarial absorbible',
            concepto_ministerio: 'Conc.Sal.04',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.05': {
            nombre_concepto_cliente: 'Conc.Sal.05',
            nombre_informe: 'Plus movilidad Geográfica',
            concepto_ministerio: 'Conc.Sal.05',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.06': {
            nombre_concepto_cliente: 'Conc.Sal.06',
            nombre_informe: 'Plus Carencia incentivos',
            concepto_ministerio: 'Conc.Sal.06',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.07': {
            nombre_concepto_cliente: 'Conc.Sal.07',
            nombre_informe: 'No concurrencia futura',
            concepto_ministerio: 'Conc.Sal.07',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.08': {
            nombre_concepto_cliente: 'Conc.Sal.08',
            nombre_informe: 'Retribución bruta',
            concepto_ministerio: 'Conc.Sal.08',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.09': {
            nombre_concepto_cliente: 'Conc.Sal.09',
            nombre_informe: '41 - PLUS TEMPORAL COMPENS.4º TURNO',
            concepto_ministerio: 'Conc.Sal.09',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.10': {
            nombre_concepto_cliente: 'Conc.Sal.10',
            nombre_informe: '42 - PLUS TURNICIDAD 4ª TURNO',
            concepto_ministerio: 'Conc.Sal.10',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.11': {
            nombre_concepto_cliente: 'Conc.Sal.11',
            nombre_informe: '43 - PLUS CONTINUIDAD DE PRODUCCION',
            concepto_ministerio: 'Conc.Sal.11',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.12': {
            nombre_concepto_cliente: 'Conc.Sal.12',
            nombre_informe: '44 - GUARDIA',
            concepto_ministerio: 'Conc.Sal.12',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.13': {
            nombre_concepto_cliente: 'Conc.Sal.13',
            nombre_informe: '46 - PLUS ESPECIAL DISPONIBILIDAD',
            concepto_ministerio: 'Conc.Sal.13',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.14': {
            nombre_concepto_cliente: 'Conc.Sal.14',
            nombre_informe: '48 - COMPLEMENTO PUESTO FIN SEMANA',
            concepto_ministerio: 'Conc.Sal.14',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.15': {
            nombre_concepto_cliente: 'Conc.Sal.15',
            nombre_informe: '50 - PLUS RESPONSABLE TURNO',
            concepto_ministerio: 'Conc.Sal.15',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.16': {
            nombre_concepto_cliente: 'Conc.Sal.16',
            nombre_informe: '53 - PLUS NOCTURNIDAD',
            concepto_ministerio: 'Conc.Sal.16',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.17': {
            nombre_concepto_cliente: 'Conc.Sal.17',
            nombre_informe: '59 - Anticipo a cuenta de objetivos',
            concepto_ministerio: 'Conc.Sal.17',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.18': {
            nombre_concepto_cliente: 'Conc.Sal.18',
            nombre_informe: '60 - COMISION',
            concepto_ministerio: 'Conc.Sal.18',
            es_salarial: true,
            es_normalizable: false,
            es_anualizable: true
        },
        'Conc.Sal.19': {
            nombre_concepto_cliente: 'Conc.Sal.19',
            nombre_informe: 'Complemento puesto',
            concepto_ministerio: 'Conc.Sal.19',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.20': {
            nombre_concepto_cliente: 'Conc.Sal.20',
            nombre_informe: 'Plus turnicidad',
            concepto_ministerio: 'Conc.Sal.20',
            es_salarial: true,
            es_normalizable: true,
            es_anualizable: true
        },
        'Conc.Sal.21': {
            nombre_concepto_cliente: 'Conc.Sal.21',
            nombre_informe: 'Horas extra',
            concepto_ministerio: 'Conc.Sal.21',
            es_salarial: true,
            es_normalizable: false,
            es_anualizable: true
        },
        'Conc.Sal.22': {
            nombre_concepto_cliente: 'Conc.Sal.22',
            nombre_informe: 'Horas presencia',
            concepto_ministerio: 'Conc.Sal.22',
            es_salarial: true,
            es_normalizable: false,
            es_anualizable: true
        },
        'C.Extra.01': {
            nombre_concepto_cliente: 'C.Extra.01',
            nombre_informe: 'Dietas y gastos',
            concepto_ministerio: 'C.Extra.01',
            es_salarial: false,
            es_normalizable: false,
            es_anualizable: true
        },
        'C.Extra.02': {
            nombre_concepto_cliente: 'C.Extra.02',
            nombre_informe: 'Prest. Enfermedad empresa',
            concepto_ministerio: 'C.Extra.02',
            es_salarial: false,
            es_normalizable: true,
            es_anualizable: true
        },
        'C.Extra.03': {
            nombre_concepto_cliente: 'C.Extra.03',
            nombre_informe: 'Enfermedad',
            concepto_ministerio: 'C.Extra.03',
            es_salarial: false,
            es_normalizable: true,
            es_anualizable: true
        },
        'C.Extra.04': {
            nombre_concepto_cliente: 'C.Extra.04',
            nombre_informe: 'Accidente',
            concepto_ministerio: 'C.Extra.04',
            es_salarial: false,
            es_normalizable: true,
            es_anualizable: true
        },
        'C.Extra.05': {
            nombre_concepto_cliente: 'C.Extra.05',
            nombre_informe: 'Complemento IT',
            concepto_ministerio: 'C.Extra.05',
            es_salarial: false,
            es_normalizable: true,
            es_anualizable: true
        }
    };

    const TECHNICAL_COLUMNS = new Set([
        normalizeHeader('% anualiz'),
        normalizeHeader('% normaliz'),
        normalizeHeader('% norm y anualiz'),
        normalizeHeader('% equiparacion'),
        normalizeHeader('% equiparación'),

        normalizeHeader('check periodo ref'),
        normalizeHeader('check equiparado'),
        normalizeHeader('check n sc norm'),
        normalizeHeader('check nº sc norm'),
        normalizeHeader('check n sc anualiz'),
        normalizeHeader('check nº sc anualiz'),
        normalizeHeader('check n sc norm y anualiz'),
        normalizeHeader('check nº sc norm y anualiz'),
        normalizeHeader('check equiparacion'),
        normalizeHeader('check equiparación'),

        normalizeHeader('f.fin.cal'),
        normalizeHeader('f fin cal'),
        normalizeHeader('fecha fin calculada'),
        normalizeHeader('f.ini.cal'),
        normalizeHeader('f ini cal'),
        normalizeHeader('fecha inicio calculada'),

        normalizeHeader('salario base ef'),
        normalizeHeader('salario base efectivo'),
        normalizeHeader('salario base eq'),
        normalizeHeader('salario base equiparado'),

        normalizeHeader('tot compl.sal ef'),
        normalizeHeader('tot compl sal ef'),
        normalizeHeader('total complementos salariales ef'),
        normalizeHeader('total complementos salariales efectivo'),

        normalizeHeader('tot compl.sal eq'),
        normalizeHeader('tot compl sal eq'),
        normalizeHeader('total complementos salariales eq'),
        normalizeHeader('total complementos salariales equiparado'),

        normalizeHeader('tot extrasalarial ef'),
        normalizeHeader('total extrasalarial ef'),
        normalizeHeader('total extrasalarial efectivo'),

        normalizeHeader('tot extrasalarial eq'),
        normalizeHeader('total extrasalarial eq'),
        normalizeHeader('total extrasalarial equiparado'),

        normalizeHeader('total salario ef'),
        normalizeHeader('total salario efectivo'),
        normalizeHeader('total salario eq'),
        normalizeHeader('total salario equiparado')
    ]);

    const getKnownConceptRule = (columnName: string): ReglaConcepto | null => {
        const trimmed = String(columnName || '').trim();
        const exact = KNOWN_CONCEPT_RULES[trimmed];

        if (exact) {
            return { ...exact };
        }

        const normalized = normalizeHeader(trimmed);
        const matchedKey = Object.keys(KNOWN_CONCEPT_RULES).find(
            key => normalizeHeader(key) === normalized
        );

        return matchedKey ? { ...KNOWN_CONCEPT_RULES[matchedKey] } : null;
    };

    const isTechnicalColumn = (columnName: string): boolean => {
        const normalized = normalizeHeader(columnName);

        if (TECHNICAL_COLUMNS.has(normalized)) {
            return true;
        }

        const technicalPatterns = [
            /^f fin cal$/,
            /^f ini cal$/,
            /^fecha fin calculada$/,
            /^fecha inicio calculada$/,
            /^salario base (ef|eq|efectivo|equiparado)$/,
            /^tot compl sal (ef|eq)$/,
            /^total complementos salariales (ef|eq|efectivo|equiparado)$/,
            /^tot extrasalarial (ef|eq)$/,
            /^total extrasalarial (ef|eq|efectivo|equiparado)$/,
            /^total salario (ef|eq|efectivo|equiparado)$/
        ];

        return technicalPatterns.some(pattern => pattern.test(normalized));
    };

    const buildAutoStructuralMapping = (headers: string[]): Record<string, string> => {
        const autoMap: Record<string, string> = {};

        AUDITOR_FIELDS.forEach(field => {
            const keyNorm = normalizeHeader(field.key);
            const labelNorm = normalizeHeader(field.label);
            const aliases = (FIELD_ALIASES[field.key] || []).map(normalizeHeader);

            const match = headers.find(h => {
                const hNorm = normalizeHeader(h);
                return hNorm === keyNorm || hNorm === labelNorm || aliases.includes(hNorm);
            });

            if (match) autoMap[field.key] = match;
        });

        return autoMap;
    };

    const getDefaultNombreInforme = (columnName: string): string => {
        const trimmed = String(columnName || '').trim();
        const knownRule = getKnownConceptRule(trimmed);

        if (knownRule) {
            return knownRule.nombre_informe;
        }

        return CONCEPT_NAME_CATALOG[trimmed] || trimmed;
    };

    // Estado Global
    const [activeStep, setActiveStep] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [auditId, setAuditId] = useState<string | null>(null);

    // Estado Fase 1: Carga y Mapeo Estructural
    const [fileState, setFileState] = useState<LocalFileState>({
        file: null,
        headers: [],
        clientName: '',
        anioAnalisis: '2024',
        selectedSheet: null,
        headerRowIndex: null
    });
    const [structuralMapping, setStructuralMapping] = useState<Record<string, string>>({});
    const [normalizationResult, setNormalizationResult] = useState<AuditorFileNormalizationResult | null>(null);
    const [normalizationReviewOpen, setNormalizationReviewOpen] = useState<boolean>(false);
    const [normalizationReviewSheetName, setNormalizationReviewSheetName] = useState<string>('');
    const [normalizationReviewHeaderRowInput, setNormalizationReviewHeaderRowInput] = useState<string>('1');
    const [isReviewingNormalization, setIsReviewingNormalization] = useState<boolean>(false);

    // Estado Fase 2: Configuración de Conceptos
    const [conceptRules, setConceptRules] = useState<ReglaConcepto[]>([]);

    // Estado Fase 3: Resultados
    const [stats, setStats] = useState<AuditCalculationStats | null>(null);

    // Configuración documental del informe.
    // El perfil ampliado conserva por seguridad su presentación detallada.
    // El perfil del despacho propone por defecto la presentación porcentual resumida.
    const [reportProfileId, setReportProfileId] = useState<ReportProfileId>('proa_ampliado_v1');
    const [comparisonDisplayMode, setComparisonDisplayMode] = useState<ComparisonDisplayMode>('amounts_and_percentages');

    // Estado Modo Premium (Checklist cumplimiento formal)
    const [complianceMode, setComplianceMode] = useState<ComplianceMode>('standard');
    const [compliance, setCompliance] = useState<ComplianceContext>({
    plan_igualdad_existe: null,
    plan_igualdad_vigente: null,
    plan_igualdad_registrado_regcon: null,
    consulta_rlt_realizada: null,
    registro_realizado_herramienta_ministerio: null,
    valoracion_puestos_herramienta_ministerio: null,
    verificacion_modelo_190_aeat: null,
    coincide_num_perceptores_modelo_190: null,
    trabajo_distancia_implantado: null,
    ett_utilizadas: null,
    erte_aplicado: null,
    inaplicacion_convenio: null,
    mecanismo_red: null,
    observaciones: ''
    });

    // --- LOGICA FASE 1: LECTURA LOCAL Y UPLOAD ---

    const onDrop = useCallback(async (acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        setIsLoading(true);
        setNormalizationResult(null);
        setNormalizationReviewOpen(false);

        try {
            const normalization = await analyzeAuditorFileNormalization(file);
            const headers = normalization.detected_columns || [];
            const autoMap = buildAutoStructuralMapping(headers);

            setNormalizationResult(normalization);
            setNormalizationReviewSheetName(normalization.selected_sheet || '');
            setNormalizationReviewHeaderRowInput(String((normalization.header_row_index ?? 0) + 1));

            setFileState(prev => ({
                ...prev,
                file,
                headers,
                selectedSheet: normalization.selected_sheet,
                headerRowIndex: normalization.header_row_index
            }));

            setStructuralMapping(autoMap);

            if (normalization.needs_review) {
                setNormalizationReviewOpen(true);
                enqueueSnackbar('Archivo analizado. Revisa la hoja o fila de cabecera propuesta.', { variant: 'warning' });
            } else {
                enqueueSnackbar('Archivo analizado. Cabeceras detectadas automáticamente.', { variant: 'info' });
            }
        } catch (err: any) {
            enqueueSnackbar(err.message || 'Error analizando el archivo.', { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    }, [enqueueSnackbar]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'text/csv': ['.csv']
        },
        multiple: false
    });

    const handleOpenNormalizationReview = () => {
        if (!normalizationResult) return;

        setNormalizationReviewSheetName(normalizationResult.selected_sheet || '');
        setNormalizationReviewHeaderRowInput(String((normalizationResult.header_row_index ?? 0) + 1));
        setNormalizationReviewOpen(true);
    };

    const handleCancelNormalizationReview = () => {
        if (isReviewingNormalization) return;

        setNormalizationReviewOpen(false);

        if (normalizationResult) {
            setNormalizationReviewSheetName(normalizationResult.selected_sheet || '');
            setNormalizationReviewHeaderRowInput(String((normalizationResult.header_row_index ?? 0) + 1));
        }
    };

    const applyNormalizationSelection = async (
        sheetName: string | null,
        headerRowInput: string,
        closeAfterApply: boolean
    ) => {
        if (!fileState.file) return;

        const parsedHeaderRow = Number(headerRowInput);
        if (!Number.isInteger(parsedHeaderRow) || parsedHeaderRow < 1) {
            enqueueSnackbar('La fila de cabecera debe ser un número entero mayor o igual que 1.', { variant: 'warning' });
            return;
        }

        setIsReviewingNormalization(true);
        try {
            const reviewed = await reviewAuditorFileNormalization(
                fileState.file,
                parsedHeaderRow - 1,
                sheetName || null
            );

            const headers = reviewed.detected_columns || [];
            const autoMap = buildAutoStructuralMapping(headers);

            setNormalizationResult(reviewed);
            setNormalizationReviewSheetName(reviewed.selected_sheet || '');
            setNormalizationReviewHeaderRowInput(String((reviewed.header_row_index ?? 0) + 1));

            setFileState(prev => ({
                ...prev,
                headers,
                selectedSheet: reviewed.selected_sheet,
                headerRowIndex: reviewed.header_row_index
            }));

            setStructuralMapping(autoMap);

            if (closeAfterApply) {
                setNormalizationReviewOpen(false);
                enqueueSnackbar('Normalización aplicada. Mapeo estructural actualizado.', { variant: 'success' });
            }
        } catch (err: any) {
            enqueueSnackbar(err.message || 'No se pudo aplicar la revisión de normalización.', { variant: 'error' });
        } finally {
            setIsReviewingNormalization(false);
        }
    };

    const handleChangeNormalizationSheet = async (nextSheetName: string) => {
        setNormalizationReviewSheetName(nextSheetName);

        await applyNormalizationSelection(
            nextSheetName || null,
            normalizationReviewHeaderRowInput,
            false
        );
    };

    const handleApplyNormalizationReview = async () => {
        await applyNormalizationSelection(
            normalizationReviewSheetName || null,
            normalizationReviewHeaderRowInput,
            true
        );
    };

    const handleStructuralSubmit = async () => {
        if (!fileState.file) return;

        const normalizedClientName = String(fileState.clientName || '').trim();

        if (!normalizedClientName) {
            enqueueSnackbar(
                'Debe indicar la denominación de la empresa antes de procesar el expediente.',
                { variant: 'warning' }
            );
            return;
        }
        
        const missing = AUDITOR_FIELDS.filter(f => f.required && !structuralMapping[f.key]);
        if (missing.length > 0) {
            enqueueSnackbar(`Faltan campos obligatorios: ${missing.map(m => m.label).join(', ')}`, { variant: 'warning' });
            return;
        }

        setIsLoading(true);
        try {
            const response = await ingestAuditFile(
                fileState.file, 
                normalizedClientName, 
                structuralMapping,
                {
                    sheet_name: fileState.selectedSheet,
                    header_row_index: fileState.headerRowIndex
                }
            );

            if (response.status === 'error') {
                throw new Error(response.message || "Error en la validación de datos.");
            }

            setAuditId(response.audit_id);

            const backendStructuralMapping = response.concept_profile?.known_template?.structural_mapping || {};
            const effectiveStructuralMapping: Record<string, string> = {
                ...structuralMapping,
                ...backendStructuralMapping
            };

            setStructuralMapping(effectiveStructuralMapping);
            
            const mappedHeaders = Object.values(effectiveStructuralMapping).filter(Boolean);
            const extraColumns = fileState.headers.filter(h => {
                const name = String(h || '').trim();

                if (!name) {
                    return false;
                }

                if (mappedHeaders.includes(name)) {
                    return false;
                }

                if (isTechnicalColumn(name)) {
                    return false;
                }

                return true;
            });
            
            // INICIALIZACIÓN DE REGLAS (Backend-driven + plantilla conocida)
            // Prioridad:
            // 1) known_template.concept_rules del backend
            // 2) catálogo local conocido
            // 3) profile.columns del backend
            // 4) fallback conservador: IGNORAR COLUMNA
            const profileCols = response.concept_profile?.columns || [];
            const profileMap = new Map(profileCols.map(p => [String(p.column).trim(), p]));

            const backendKnownRules = response.concept_profile?.known_template?.concept_rules || [];
            const backendKnownRuleMap = new Map(
                backendKnownRules.map(rule => [String(rule.nombre_concepto_cliente).trim(), rule])
            );

            const initialRules: ReglaConcepto[] = extraColumns.map((col) => {
                const name = String(col || '').trim();

                const backendKnownRule = backendKnownRuleMap.get(name);
                if (backendKnownRule) {
                    return {
                        nombre_concepto_cliente: name,
                        nombre_informe: backendKnownRule.nombre_informe,
                        concepto_ministerio: backendKnownRule.concepto_ministerio || backendKnownRule.codigo_ministerio || name,
                        es_salarial: !!backendKnownRule.es_salarial,
                        es_normalizable: !!backendKnownRule.es_normalizable,
                        es_anualizable: !!backendKnownRule.es_anualizable
                    };
                }

                const localKnownRule = getKnownConceptRule(name);
                if (localKnownRule) {
                    return localKnownRule;
                }

                const prof = profileMap.get(name);

                if (!prof) {
                    return {
                        nombre_concepto_cliente: name,
                        nombre_informe: getDefaultNombreInforme(name),
                        concepto_ministerio: "IGNORAR COLUMNA",
                        es_salarial: true,
                        es_normalizable: true,
                        es_anualizable: true
                    };
                }

                const tipo = prof.suggested_tipo_retribucion;
                const include = !!prof.suggested_include && tipo !== "Excluir";

                return {
                    nombre_concepto_cliente: name,
                    nombre_informe: getDefaultNombreInforme(name),
                    concepto_ministerio: include ? "AUTO" : "IGNORAR COLUMNA",
                    es_salarial: tipo !== "Extrasalarial",
                    es_normalizable: !!prof.suggested_normalizable,
                    es_anualizable: !!prof.suggested_anualizable
                };
            });

            setConceptRules(initialRules);
            setActiveStep(1);
            enqueueSnackbar(`Datos ingeridos. Detectados ${extraColumns.length} conceptos adicionales.`, { variant: 'success' });

        } catch (err: any) {
            enqueueSnackbar(err.message, { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    // --- LOGICA FASE 2: CONFIGURACIÓN DE CONCEPTOS ---

    const updateRule = (index: number, field: keyof ReglaConcepto, value: any) => {
        const newRules = [...conceptRules];
        newRules[index] = { ...newRules[index], [field]: value };
        setConceptRules(newRules);
    };

    const applyNombreInformeSuggested = () => {
        setConceptRules(prev =>
            prev.map(rule => ({
                ...rule,
                nombre_informe: getDefaultNombreInforme(rule.nombre_concepto_cliente)
            }))
        );
    };

    const applyNombreInformeOriginal = () => {
        setConceptRules(prev =>
            prev.map(rule => ({
                ...rule,
                nombre_informe: String(rule.nombre_concepto_cliente || '').trim()
            }))
        );
    };

    const handleConceptCalculation = async () => {
        if (!auditId) return;
        setIsLoading(true);
        try {
            // PROTOCOLO DE LIMPIEZA: Filtramos cualquier regla marcada como IGNORAR
            // Solo pasan al backend las reglas que tienen un código Conc.Sal.XX o C.Extra.XX válido
            const activeRules = conceptRules.filter(r =>
                !!r.nombre_concepto_cliente &&
                r.concepto_ministerio !== "IGNORAR COLUMNA"
            );
            
            console.log(`[Auditor] Enviando ${activeRules.length} reglas activas al motor de cálculo.`);

            if (activeRules.length === 0 && conceptRules.length > 0) {
                 enqueueSnackbar('Se aplicará clasificación automática de conceptos (backend). Puedes ajustar el mapeo si deseas máxima precisión.', { variant: 'info' });
            }

            const anioAnalisisNum = Number(fileState.anioAnalisis);

            if (!Number.isInteger(anioAnalisisNum) || anioAnalisisNum < 2000 || anioAnalisisNum > 2100) {
                enqueueSnackbar('Introduce un año de análisis válido.', { variant: 'warning' });
                setIsLoading(false);
                return;
            }

            const results = await calculateAuditStats(auditId, activeRules, anioAnalisisNum);
            setStats(results);
            setActiveStep(2);
            enqueueSnackbar('Motor de cálculo ejecutado con éxito.', { variant: 'success' });
        } catch (err: any) {
            console.error(err);
            enqueueSnackbar(err.message || "Error en el cálculo.", { variant: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const setComplianceFlag = (key: keyof ComplianceContext, value: boolean | null) => {
        setCompliance(prev => ({ ...prev, [key]: value }));
    };

    const handleReportProfileChange = (nextProfileId: ReportProfileId) => {
        setReportProfileId(nextProfileId);

        if (nextProfileId === 'despacho_validado_v1') {
            setComparisonDisplayMode('percentages_only');
            return;
        }

        // El modelo ampliado estable no implementa todavía una variante resumida.
        setComparisonDisplayMode('amounts_and_percentages');
    };

    // --- LOGICA FASE 3: REPORTE ---

    const handleDownload = async () => {
        if (!auditId) return;

        const normalizedClientName = String(fileState.clientName || '').trim();

        if (!normalizedClientName) {
            enqueueSnackbar(
                'Debe indicar la denominación de la empresa antes de generar el informe final.',
                { variant: 'warning' }
            );
            return;
        }

        try {
            // 1) Persistimos modo/checklist ANTES de generar el Word (para trazabilidad premium)
            await upsertAuditCompliance(auditId, {
                mode: complianceMode,
                compliance: complianceMode === 'premium' ? compliance : null
            });

            // 2) Generamos informe con el perfil documental seleccionado.
            await downloadAuditReport(
                auditId,
                normalizedClientName,
                {
                    reportProfileId,
                    comparisonDisplayMode
                }
            );

            enqueueSnackbar('Informe descargado.', { variant: 'success' });
        } catch (err: any) {
            enqueueSnackbar(err.message || 'Error generando el informe Word.', { variant: 'error' });
        }
    };

    // --- RENDERERS ---

    const renderUploadStep = () => {
        const visibleFields = AUDITOR_FIELDS;

        return (
            <Grid container spacing={4}>
                <Grid item xs={12} md={5}>
                    <Box {...getRootProps()} sx={uploadZoneSx}>
                        <input {...getInputProps()} />
                        <CloudUploadIcon sx={{ fontSize: 60, color: isDragActive ? '#38bdf8' : '#64748b', mb: 2 }} />
                        <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 600 }}>
                            {fileState.file ? fileState.file.name : 'Arrastre su Excel de Nómina aquí'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                            Soporta .xlsx y .csv
                        </Typography>
                    </Box>
                    
                    <TextField
                        fullWidth
                        required
                        label="Denominación de la empresa"
                        variant="outlined"
                        sx={{ mt: 3, bgcolor: 'rgba(255,255,255,0.02)' }}
                        value={fileState.clientName}
                        error={fileState.clientName.trim().length === 0}
                        helperText={
                            fileState.clientName.trim().length === 0
                                ? 'Campo obligatorio para generar el expediente.'
                                : 'Esta denominación aparecerá en el informe final.'
                        }
                        onChange={(e) =>
                            setFileState(prev => ({
                                ...prev,
                                clientName: e.target.value
                            }))
                        }
                    />
                    <FormControl fullWidth sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.02)' }}>
                        <InputLabel sx={{ color: '#94a3b8' }}>Año de análisis</InputLabel>
                        <Select
                            value={fileState.anioAnalisis}
                            label="Año de análisis"
                            onChange={(e) => setFileState(prev => ({ ...prev, anioAnalisis: String(e.target.value) }))}
                            sx={{ 
                                '& .MuiSelect-select': { color: '#f8fafc' },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' }
                            }}
                        >
                            {[2022, 2023, 2024, 2025, 2026].map((year) => (
                                <MenuItem key={year} value={String(year)}>{year}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={7}>
                    <Paper sx={{ p: 3, bgcolor: 'rgba(15, 23, 42, 0.6)', borderRadius: 2, height: '100%' }}>
                        <Typography variant="subtitle1" sx={{ color: '#38bdf8', fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center' }}>
                            <SettingsSuggestIcon sx={{ mr: 1 }} />
                            Mapeo estructural
                        </Typography>

                        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                            Primero complete los campos obligatorios. Las agrupaciones y checks son opcionales, pero recomendables para reproducir el modelo completo.
                        </Typography>

                        {normalizationResult && (
                            <Alert
                                severity={normalizationResult.warnings.length > 0 ? 'warning' : 'info'}
                                sx={{ mb: 2 }}
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        variant="outlined"
                                        onClick={handleOpenNormalizationReview}
                                        disabled={isReviewingNormalization}
                                    >
                                        Revisar
                                    </Button>
                                }
                            >
                                <AlertTitle>
                                    {normalizationResult.source === 'manual_review'
                                        ? 'Normalización revisada'
                                        : 'Normalización automática'}
                                </AlertTitle>
                                {normalizationResult.selected_sheet
                                    ? `Hoja: ${normalizationResult.selected_sheet} · `
                                    : ''}
                                Cabecera: fila {normalizationResult.header_row_index + 1}
                                {normalizationResult.warnings.map((warning, index) => (
                                    <Typography key={`${warning.code}-${index}`} variant="body2">
                                        {warning.message}
                                    </Typography>
                                ))}
                            </Alert>
                        )}

                        {!fileState.file ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#64748b' }}>
                                <Typography>Suba un archivo para configurar las columnas.</Typography>
                            </Box>
                        ) : (
                            <Grid container spacing={2} sx={{ maxHeight: '400px', overflowY: 'auto', pr: 1 }}>
                                {visibleFields.map((field: AuditorField) => (
                                    <Grid item xs={12} key={field.key}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel sx={{ color: '#94a3b8' }}>
                                                {field.label} {field.required ? '*' : ''}
                                            </InputLabel>
                                            <Select
                                                value={structuralMapping[field.key] || ''}
                                                label={`${field.label} ${field.required ? '*' : ''}`}
                                                onChange={(e) => setStructuralMapping({ ...structuralMapping, [field.key]: e.target.value })}
                                                sx={{ 
                                                    '& .MuiSelect-select': { color: '#f8fafc' },
                                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' }
                                                }}
                                            >
                                                <MenuItem value=""><em>Ignorar</em></MenuItem>
                                                {fileState.headers.map(h => (
                                                    <MenuItem key={h} value={h}>{h}</MenuItem>
                                                ))}
                                            </Select>
                                            {field.tooltip && (
                                                <Typography variant="caption" sx={{ color: '#64748b', ml: 1 }}>
                                                    {field.tooltip}
                                                </Typography>
                                            )}
                                        </FormControl>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                        
                        <Button 
                            fullWidth 
                            variant="contained" 
                            size="large"
                            disabled={
                                !fileState.file ||
                                !fileState.clientName.trim() ||
                                isLoading
                            }
                            onClick={handleStructuralSubmit}
                            sx={{ mt: 3, bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700 }}
                        >
                            {isLoading ? <CircularProgress size={24} /> : 'Procesar e Ingestar'}
                        </Button>
                    </Paper>
                </Grid>
            </Grid>
        );
    };

    const renderConceptConfigStep = () => {
        const includedCount = conceptRules.filter(r => r.concepto_ministerio !== "IGNORAR COLUMNA").length;
        const unassignedCount = conceptRules.length - includedCount;

        // UX: mostrar primero los conceptos incluidos, luego los no asignados (mismo efecto técnico que “ignorado”).
        // Importante: conservamos el índice original para que updateRule actúe sobre el elemento correcto.
        const displayedRules = conceptRules
            .map((r, originalIndex) => ({ rule: r, originalIndex }))
            .sort((a, b) => {
                const aIgnored = a.rule.concepto_ministerio === "IGNORAR COLUMNA";
                const bIgnored = b.rule.concepto_ministerio === "IGNORAR COLUMNA";
                if (aIgnored !== bIgnored) return aIgnored ? 1 : -1;
                return String(a.rule.nombre_concepto_cliente || "").localeCompare(
                    String(b.rule.nombre_concepto_cliente || ""),
                    'es',
                    { sensitivity: 'base' }
                );
            });

        return (
            <Box>
                <Alert severity="info" sx={{ mb: 3, bgcolor: 'rgba(56, 189, 248, 0.1)', color: '#bae6fd', border: '1px solid #38bdf8' }}>
                    <AlertTitle>Clasificación de conceptos</AlertTitle>
                    Hemos detectado <strong>{conceptRules.length}</strong> conceptos adicionales: <strong>{includedCount}</strong> incluidos · <strong>{unassignedCount}</strong> no asignados.
                    <Box sx={{ mt: 1, color: '#94a3b8', fontSize: '0.85rem' }}>
                        Si no revisa esta pantalla, el sistema aplicará la clasificación automática en backend.
                    </Box>
                </Alert>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 1.5 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={applyNombreInformeSuggested}
                        sx={{
                            borderColor: '#475569',
                            color: '#94a3b8',
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#64748b',
                                bgcolor: 'rgba(148, 163, 184, 0.06)'
                            }
                        }}
                    >
                        Usar nombre sugerido
                    </Button>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={applyNombreInformeOriginal}
                        sx={{
                            borderColor: '#475569',
                            color: '#94a3b8',
                            fontWeight: 600,
                            textTransform: 'none',
                            '&:hover': {
                                borderColor: '#64748b',
                                bgcolor: 'rgba(148, 163, 184, 0.06)'
                            }
                        }}
                    >
                        Usar nombre original
                    </Button>
                </Box>

                <TableContainer component={Paper} sx={{ bgcolor: 'rgba(15, 23, 42, 0.6)', mb: 3 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#0f172a' }}>
                                <TableCell sx={{ color: '#94a3b8' }}>Concepto (columna Excel)</TableCell>
                                <TableCell sx={{ color: '#94a3b8' }}>Nombre en informe</TableCell>
                                <TableCell align="center" sx={{ color: '#94a3b8' }}>Incluir en cálculo</TableCell>
                                <TableCell align="center" sx={{ color: '#94a3b8' }}>Tipo</TableCell>
                                <TableCell align="center" sx={{ color: '#94a3b8' }}>Normalizable (Jornada)</TableCell>
                                <TableCell align="center" sx={{ color: '#94a3b8' }}>Anualizable (Periodo)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {displayedRules.map(({ rule, originalIndex }) => {
                                const isIgnored = rule.concepto_ministerio === "IGNORAR COLUMNA";

                                return (
                                    <TableRow
                                        key={`${rule.nombre_concepto_cliente}-${originalIndex}`}
                                        hover
                                        sx={{
                                            '&:hover': { bgcolor: isIgnored ? 'rgba(148, 163, 184, 0.06)' : 'rgba(255,255,255,0.02)' },
                                            bgcolor: isIgnored ? 'rgba(148, 163, 184, 0.03)' : 'transparent',
                                            transition: 'background-color 0.2s'
                                        }}
                                    >
                                        {/* COLUMNA 1: NOMBRE ORIGINAL */}
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    color: isIgnored ? '#94a3b8' : '#f8fafc',
                                                    fontWeight: 600,
                                                    fontStyle: isIgnored ? 'italic' : 'normal',
                                                    opacity: isIgnored ? 0.85 : 1
                                                }}
                                            >
                                                {rule.nombre_concepto_cliente}
                                            </Typography>
                                            {isIgnored && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        display: 'block',
                                                        color: '#94a3b8',
                                                        fontSize: '0.7rem'
                                                    }}
                                                >
                                                    No asignado
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* COLUMNA 2: NOMBRE DOCUMENTAL / INFORME */}
                                        <TableCell>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                value={rule.nombre_informe || ''}
                                                onChange={(e) => updateRule(originalIndex, 'nombre_informe', e.target.value)}
                                                disabled={isIgnored}
                                                sx={{
                                                    minWidth: 260,
                                                    '& .MuiInputBase-root': {
                                                        bgcolor: isIgnored ? 'rgba(148, 163, 184, 0.04)' : 'rgba(255,255,255,0.015)'
                                                    },
                                                    '& .MuiInputBase-input': {
                                                        color: isIgnored ? '#64748b' : '#cbd5e1',
                                                        fontSize: '0.9rem',
                                                        fontWeight: 400
                                                    },
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#2f3b52'
                                                    },
                                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#3b4b66'
                                                    },
                                                    '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#64748b'
                                                    }
                                                }}
                                            />
                                        </TableCell>

                                        {/* COLUMNA 3: TOGGLE INCLUIR/EXCLUIR (sin Ministerio) */}
                                        <TableCell align="center">
                                            <Switch
                                                checked={!isIgnored}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    updateRule(originalIndex, 'concepto_ministerio', checked ? "AUTO" : "IGNORAR COLUMNA");
                                                }}
                                                size="small"
                                                color="success"
                                            />
                                        </TableCell>

                                        {/* COLUMNA 3: TIPO (SALARIAL/EXTRA) */}
                                        <TableCell align="center">
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={rule.es_salarial}
                                                        onChange={(e) => updateRule(originalIndex, 'es_salarial', e.target.checked)}
                                                        color="success"
                                                        size="small"
                                                        disabled={isIgnored} // BLOQUEO TÁCTICO
                                                    />
                                                }
                                                label={
                                                    <Typography variant="caption" color={isIgnored ? 'text.disabled' : (rule.es_salarial ? '#4ade80' : '#f472b6')}>
                                                        {isIgnored ? '---' : (rule.es_salarial ? 'Salarial' : 'Extra.')}
                                                    </Typography>
                                                }
                                            />
                                        </TableCell>

                                        {/* COLUMNA 4: NORMALIZABLE */}
                                        <TableCell align="center">
                                            <Switch
                                                checked={rule.es_normalizable}
                                                onChange={(e) => updateRule(originalIndex, 'es_normalizable', e.target.checked)}
                                                color="warning"
                                                size="small"
                                                disabled={isIgnored}
                                            />
                                        </TableCell>

                                        {/* COLUMNA 5: ANUALIZABLE */}
                                        <TableCell align="center">
                                            <Switch
                                                checked={rule.es_anualizable}
                                                onChange={(e) => updateRule(originalIndex, 'es_anualizable', e.target.checked)}
                                                color="warning"
                                                size="small"
                                                disabled={isIgnored}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={handleConceptCalculation}
                        sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 700 }}
                        disabled={isLoading}
                    >
                        {isLoading ? <CircularProgress size={20} /> : 'Calcular'}
                    </Button>
                </Box>
            </Box>
        );
    };

    const renderResultsStep = () => (
        <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: '#4ade80', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#f8fafc', fontWeight: 700, mb: 1 }}>
                Auditoría Finalizada
            </Typography>
            <Typography sx={{ color: '#94a3b8', mb: 4 }}>
                Los cálculos de brecha salarial, promedios y equiparaciones se han completado correctamente.
            </Typography>

            <Grid container spacing={3} sx={{ mb: 4, textAlign: 'left' }}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid #334155' }}>
                        <CardContent>
                            <Typography variant="overline" color="text.secondary">Total Empleados</Typography>
                            <Typography variant="h5" color="#f8fafc">
                                {stats?.tabla_promedios ? 'Procesados OK' : '---'}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                     <Card sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid #334155' }}>
                        <CardContent>
                            <Typography variant="overline" color="text.secondary">Brecha Bruta Global</Typography>
                             <Typography variant="h5" color="#38bdf8">Calculada</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                     <Card sx={{ bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid #334155' }}>
                        <CardContent>
                            <Typography variant="overline" color="text.secondary">Estado Legal</Typography>
                             <Chip label="Listo para Informe" color="success" size="small" variant="outlined" />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Divider sx={{ my: 4, borderColor: '#334155' }} />

            <Box sx={{ textAlign: 'left', maxWidth: 900, mx: 'auto', mb: 4 }}>
                <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700, mb: 1 }}>
                    Configuración documental
                </Typography>

                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                    Selecciona el modelo de redacción y el nivel de detalle de las comparaciones que aparecerán en el informe final.
                </Typography>

                <Paper
                    sx={{
                        p: 2.5,
                        bgcolor: 'rgba(15, 23, 42, 0.6)',
                        border: '1px solid #334155'
                    }}
                >
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: '#94a3b8' }}>
                                    Modelo de informe
                                </InputLabel>

                                <Select
                                    value={reportProfileId}
                                    label="Modelo de informe"
                                    onChange={(e) => handleReportProfileChange(e.target.value as ReportProfileId)}
                                    sx={{
                                        '& .MuiSelect-select': { color: '#f8fafc' },
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' }
                                    }}
                                >
                                    <MenuItem value="proa_ampliado_v1">
                                        Modelo ampliado
                                    </MenuItem>

                                    <MenuItem value="despacho_validado_v1">
                                        Modelo validado por el despacho
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 1 }}>
                                {reportProfileId === 'despacho_validado_v1'
                                    ? 'Estructura y estilo documental basados en el modelo consolidado del despacho.'
                                    : 'Informe ampliado con desarrollo metodológico y detalle cuantitativo completo.'}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth size="small">
                                <InputLabel sx={{ color: '#94a3b8' }}>
                                    Presentación de comparaciones
                                </InputLabel>

                                <Select
                                    value={comparisonDisplayMode}
                                    label="Presentación de comparaciones"
                                    disabled={reportProfileId === 'proa_ampliado_v1'}
                                    onChange={(e) => setComparisonDisplayMode(e.target.value as ComparisonDisplayMode)}
                                    sx={{
                                        '& .MuiSelect-select': { color: '#f8fafc' },
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' }
                                    }}
                                >
                                    <MenuItem value="percentages_only">
                                        Resumida — porcentajes
                                    </MenuItem>

                                    <MenuItem value="amounts_and_percentages">
                                        Detallada — importes y porcentajes
                                    </MenuItem>
                                </Select>
                            </FormControl>

                            <Typography variant="caption" sx={{ display: 'block', color: '#64748b', mt: 1 }}>
                                {reportProfileId === 'proa_ampliado_v1'
                                    ? 'El modelo ampliado conserva su presentación detallada validada.'
                                    : comparisonDisplayMode === 'percentages_only'
                                        ? 'Recomendada para el modelo del despacho: prioriza porcentajes y reduce el detalle monetario en el cuerpo principal.'
                                        : 'Incluye importes absolutos, porcentajes, promedios y medianas.'}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>

            <Divider sx={{ my: 4, borderColor: '#334155' }} />

            <Box sx={{ textAlign: 'left', maxWidth: 900, mx: 'auto', mb: 3 }}>
            <Typography variant="h6" sx={{ color: '#f8fafc', fontWeight: 700, mb: 1 }}>
                Verificaciones formales y documentales
            </Typography>

            <FormControlLabel
                control={
                <Switch
                    checked={complianceMode === 'premium'}
                    onChange={(e) => setComplianceMode(e.target.checked ? 'premium' : 'standard')}
                    color="success"
                />
                }
                label={
                <Typography sx={{ color: complianceMode === 'premium' ? '#4ade80' : '#94a3b8', fontWeight: 600 }}>
                    {complianceMode === 'premium'
                    ? 'Incluir verificaciones formales y documentales declaradas'
                    : 'No incluir verificaciones formales y documentales adicionales'}
                </Typography>
                }
            />

            {complianceMode === 'premium' && (
                <Paper sx={{ mt: 2, p: 2, bgcolor: 'rgba(15, 23, 42, 0.6)', border: '1px solid #334155' }}>
                <Typography sx={{ color: '#38bdf8', fontWeight: 700, mb: 1 }}>
                    Verificaciones formales y documentales (declarativas)
                </Typography>
                <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                    Marca solo lo que puedas afirmar con evidencia. Si no consta, deja “Sin información”.
                </Typography>

                {/* Helper inline para selects triestado */}
                {([
                    ['plan_igualdad_existe', 'Plan de Igualdad existe'],
                    ['plan_igualdad_vigente', 'Plan de Igualdad vigente'],
                    ['plan_igualdad_registrado_regcon', 'Plan inscrito/publicado en RegCon'],
                    ['consulta_rlt_realizada', 'Consulta previa a RLT realizada'],
                    ['registro_realizado_herramienta_ministerio', 'Registro retributivo con herramienta oficial'],
                    ['valoracion_puestos_herramienta_ministerio', 'Valoración de puestos con herramienta oficial/equivalente'],
                    ['verificacion_modelo_190_aeat', 'Verificación documental con Modelo 190 (AEAT)'],
                    ['coincide_num_perceptores_modelo_190', 'Coincidencia número perceptores vs Modelo 190'],
                    ['trabajo_distancia_implantado', 'Trabajo a distancia implantado'],
                    ['ett_utilizadas', 'Uso de ETT en el período'],
                    ['erte_aplicado', 'ERTE aplicado en el período'],
                    ['inaplicacion_convenio', 'Inaplicación de convenio en el período'],
                    ['mecanismo_red', 'Mecanismo RED aplicado en el período']
                ] as Array<[keyof ComplianceContext, string]>).map(([k, label]) => (
                    <Grid container spacing={2} key={String(k)} sx={{ mb: 1 }}>
                    <Grid item xs={12} md={7}>
                        <Typography sx={{ color: '#f8fafc' }}>{label}</Typography>
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <FormControl fullWidth size="small">
                        <Select
                            value={(compliance[k] === null || compliance[k] === undefined) ? 'na' : (compliance[k] ? 'yes' : 'no')}
                            onChange={(e) => {
                            const v = e.target.value;
                            if (v === 'yes') setComplianceFlag(k, true);
                            else if (v === 'no') setComplianceFlag(k, false);
                            else setComplianceFlag(k, null);
                            }}
                            sx={{
                            '& .MuiSelect-select': { color: '#f8fafc' },
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' }
                            }}
                        >
                            <MenuItem value="na"><em>Sin información</em></MenuItem>
                            <MenuItem value="yes">Sí (verificado)</MenuItem>
                            <MenuItem value="no">No (no consta)</MenuItem>
                        </Select>
                        </FormControl>
                    </Grid>
                    </Grid>
                ))}

                <TextField
                    fullWidth
                    label="Observaciones / Evidencias (opcional)"
                    multiline
                    minRows={3}
                    value={compliance.observaciones || ''}
                    onChange={(e) => setCompliance(prev => ({ ...prev, observaciones: e.target.value }))}
                    sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.02)' }}
                />
                </Paper>
            )}
            </Box>

            <Divider sx={{ my: 4, borderColor: '#334155' }} />

            <Button 
                variant="contained" 
                size="large" 
                startIcon={<DescriptionIcon />}
                onClick={handleDownload}
                sx={{ 
                    bgcolor: '#4ade80', 
                    color: '#064e3b', 
                    fontWeight: 700, 
                    px: 4, py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': { bgcolor: '#22c55e' }
                }}
            >
                Descargar Informe Legal (.docx)
            </Button>
            
            <Typography variant="caption" display="block" sx={{ mt: 2, color: '#64748b' }}>
                Genera documento compatible con RD 902/2020 (España)
            </Typography>
        </Box>
    );

    const steps = ['Ingesta Estructural', 'Configuración Conceptos', 'Informe Legal'];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" sx={{ color: '#f8fafc', fontWeight: 700, letterSpacing: '-0.5px' }}>
                    Auditoría Retributiva IA
                </Typography>
                <Typography variant="body1" sx={{ color: '#94a3b8' }}>
                    Sistema automatizado de análisis de brecha salarial y generación de informes legales.
                </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 5 }} alternativeLabel>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel 
                            sx={{ 
                                '& .MuiStepLabel-label': { color: '#94a3b8' },
                                '& .Mui-active': { color: '#38bdf8 !important' },
                                '& .Mui-completed': { color: '#4ade80 !important' }
                            }}
                        >
                            {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Paper sx={glassCardSx}>
                {activeStep === 0 && renderUploadStep()}
                {activeStep === 1 && renderConceptConfigStep()}
                {activeStep === 2 && renderResultsStep()}
            </Paper>

            <Dialog
                open={normalizationReviewOpen}
                onClose={handleCancelNormalizationReview}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>Revisar normalización del archivo</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {normalizationResult ? (
                            <>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Hoja"
                                        value={normalizationReviewSheetName}
                                        onChange={(event) => void handleChangeNormalizationSheet(event.target.value)}
                                        disabled={isReviewingNormalization || normalizationResult.sheets.length === 0}
                                    >
                                        {(normalizationResult.sheets || []).map((sheet) => (
                                            <MenuItem key={sheet.name} value={sheet.name}>
                                                {sheet.name} · {sheet.row_count} filas · score {sheet.score}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Fila de cabecera Excel"
                                        type="number"
                                        value={normalizationReviewHeaderRowInput}
                                        onChange={(event) => setNormalizationReviewHeaderRowInput(event.target.value)}
                                        inputProps={{ min: 1, step: 1 }}
                                        disabled={isReviewingNormalization}
                                    />
                                </Stack>

                                <Box>
                                    <Typography sx={{ color: '#f8fafc', fontWeight: 800, mb: 1 }}>
                                        Vista previa cruda del archivo
                                    </Typography>

                                    <Box
                                        sx={{
                                            overflowX: 'auto',
                                            border: '1px solid rgba(148,163,184,0.18)',
                                            borderRadius: 1.5,
                                            maxHeight: 420
                                        }}
                                    >
                                        <Table size="small">
                                            <TableBody>
                                                {(normalizationResult.raw_preview_rows || []).map((row, rowIndex) => {
                                                    const isSelectedHeaderRow =
                                                        rowIndex + 1 === Number(normalizationReviewHeaderRowInput);

                                                    return (
                                                        <TableRow
                                                            key={`raw-row-${rowIndex}`}
                                                            selected={isSelectedHeaderRow}
                                                            onClick={() => setNormalizationReviewHeaderRowInput(String(rowIndex + 1))}
                                                            sx={{
                                                                cursor: 'pointer',
                                                                transition: 'background-color 0.15s ease',
                                                                '&:hover td': {
                                                                    backgroundColor: isSelectedHeaderRow
                                                                        ? 'rgba(34,211,238,0.20)'
                                                                        : 'rgba(148,163,184,0.10)',
                                                                },
                                                                ...(isSelectedHeaderRow
                                                                    ? {
                                                                        '& td': {
                                                                            backgroundColor: 'rgba(34,211,238,0.16)',
                                                                            fontWeight: 700,
                                                                        },
                                                                    }
                                                                    : {})
                                                            }}
                                                        >
                                                            <TableCell sx={{ color: '#94a3b8', fontWeight: 700 }}>
                                                                {rowIndex + 1}
                                                            </TableCell>

                                                            {row.map((cell, cellIndex) => (
                                                                <TableCell
                                                                    key={`raw-cell-${rowIndex}-${cellIndex}`}
                                                                    sx={{ color: '#cbd5e1', whiteSpace: 'nowrap' }}
                                                                >
                                                                    {cell === null || cell === undefined || String(cell).trim() === ''
                                                                        ? '—'
                                                                        : String(cell)}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </Box>

                                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 1 }}>
                                        La fila resaltada se usará como cabecera al reaplicar la normalización.
                                    </Typography>
                                </Box>
                            </>
                        ) : (
                            <Alert severity="info">No hay normalización disponible.</Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancelNormalizationReview}
                        disabled={isReviewingNormalization}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleApplyNormalizationReview}
                        disabled={isReviewingNormalization}
                    >
                        {isReviewingNormalization ? 'Aplicando...' : 'Aplicar revisión'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AuditorPage;